import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Link,
  Option,
  Select,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import { FaBell, FaBullhorn, FaCheckCircle, FaComment, FaEye, FaMoneyBillWave, FaPaperPlane, FaTimes, FaTimesCircle, FaUndo } from 'react-icons/fa';
import { format } from 'date-fns';
import { directSupabase } from '../lib/supabaseClient';
import { IconWrapper } from './IconWrapper';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useProfile } from '../hooks/useProfile';
import {
  isJobNotificationType,
  isOfficeJobViewerRole,
  jobNotificationLabel,
  jobNotificationPath,
  jobNotificationVisible,
  listAssignedJobCardIds,
} from '../services/jobNotifications';
import { workspaceDocumentPath } from '../modules/docs/utils/workspaceDocumentAccess';
import {
  CREW_ALERT_TYPE,
  crewAlertLabel,
  crewAlertVisible,
  isCrewAlertType,
  normaliseCrewAlertPriority,
} from '../services/crewAlerts';
import CrewAlertModal from './CrewAlertModal';

type NotificationRow = {
  id: string;
  created_at: string;
  type: string;
  message: string;
  is_read: boolean;
  metadata?: any;
};

type ActivityItem = {
  source: 'notification';
  id: string;
  createdAt: string;
  typeKey: string;
  typeLabel: string;
  message: string;
  isArchived: boolean;
  raw: NotificationRow;
};

const LOCAL_MIN_KEY = 'jarvis_notifications_minimised_v1';
const CENTER_LIMIT = 10;

// Only show the notifications the user explicitly wants in the widget.
const WIDGET_NOTIFICATION_TYPES = new Set<string>([
  'doc_opened',
  'doc_accepted',
  'doc_rejected',
  'doc_query',
  'lead_created',
  'quote_accepted_final_survey',
]);
const LINKABLE_REF_NOTIFICATION_TYPES = new Set<string>([
  'doc_opened',
  'doc_accepted',
  'doc_rejected',
  'doc_query',
  'quote_accepted_final_survey',
  'survey_integrity',
]);
const ADMIN_ONLY_NOTIFICATION_TYPES = new Set<string>(['survey_integrity']);

function readMinimisedIds(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(LOCAL_MIN_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, boolean>;
  } catch {
    return {};
  }
}

function writeMinimisedIds(next: Record<string, boolean>) {
  try {
    localStorage.setItem(LOCAL_MIN_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function iconForType(type: string) {
  const t = String(type || '').toLowerCase();
  if (isCrewAlertType(t)) return FaBullhorn;
  if (t === 'job_comment') return FaComment;
  if (t === 'job_completed') return FaCheckCircle;
  if (t === 'job_reopened') return FaUndo;
  if (t.includes('accepted')) return FaCheckCircle;
  if (t.includes('rejected') || t.includes('declined')) return FaTimesCircle;
  if (t.includes('query')) return FaBell;
  if (t.includes('opened') || t.includes('viewed')) return FaEye;
  if (t.includes('deposit')) return FaMoneyBillWave;
  if (t.includes('sent') || t.includes('send')) return FaPaperPlane;
  return FaBell;
}

function chipColorForType(type: string, metadata?: any): 'primary' | 'success' | 'danger' | 'warning' | 'neutral' {
  const t = String(type || '').toLowerCase();
  if (isCrewAlertType(t)) {
    const priority = normaliseCrewAlertPriority(metadata?.priority);
    return priority === 'urgent' ? 'danger' : priority === 'important' ? 'warning' : 'primary';
  }
  if (t === 'job_comment') return 'primary';
  if (t === 'job_completed') return 'success';
  if (t === 'job_reopened') return 'warning';
  if (t.includes('accepted')) return 'success';
  if (t.includes('rejected') || t.includes('declined')) return 'danger';
  if (t.includes('query')) return 'warning';
  if (t.includes('deposit')) return 'warning';
  if (t.includes('opened') || t.includes('viewed')) return 'primary';
  if (t.includes('sent') || t.includes('send')) return 'neutral';
  return 'neutral';
}

function labelForType(type: string): string {
  const t = String(type || '').trim();
  if (!t) return 'notification';
  if (t === 'doc_opened') return 'Customer Opened Quotation';
  if (t === 'doc_accepted') return 'Customer Accepted Quotation';
  if (t === 'doc_rejected') return 'Customer Rejected Quotation';
  if (t === 'doc_query') return 'Customer Query';
  if (t === 'lead_created') return 'New Lead';
  if (t === 'quote_accepted_final_survey') return 'Final Survey Ready';
  if (t === 'survey_integrity') return 'Survey Integrity';
  if (isJobNotificationType(t)) return jobNotificationLabel(t);
  return t
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function parseReferenceFromMessage(message: string): string {
  const m = String(message || '').match(/\b[A-Z]{2,}-\d+\b/);
  return m?.[0] || '';
}

function getActivityPreviewPath(notification: NotificationRow): string | null {
  const meta = notification?.metadata;
  if (!meta || typeof meta !== 'object') return null;
  const actionEmailActivityId = String(
    (meta as any).actionEmailActivityId ||
    (meta as any).last_query_email_activity_id ||
    ''
  ).trim();
  if (!actionEmailActivityId) return null;
  return `/admin?tab=email-activity&preview=${encodeURIComponent(actionEmailActivityId)}`;
}

async function resolveActivityPreviewPath(notification: NotificationRow): Promise<string> {
  const direct = getActivityPreviewPath(notification);
  if (direct) return direct;

  const meta = notification?.metadata;
  const shareLinkId = meta && typeof meta === 'object' ? String((meta as any).shareLinkId || '').trim() : '';
  if (shareLinkId) {
    try {
      const { data } = await directSupabase
        .from('email_activity_log')
        .select('id,metadata,created_at')
        .filter('metadata->>shareLinkId', 'eq', shareLinkId)
        .order('created_at', { ascending: false })
        .limit(50);

      const match = (data || []).find((row: any) => {
        const rowMeta = row?.metadata && typeof row.metadata === 'object' ? row.metadata : {};
        const action = String((rowMeta as any)?.customerAction?.action || (rowMeta as any)?.customer_action?.action || '').trim().toLowerCase();
        return action === 'query';
      });

      if (match?.id) {
        return `/admin?tab=email-activity&preview=${encodeURIComponent(String(match.id))}`;
      }
    } catch {
      // fall through to generic activity page
    }
  }

  return '/admin?tab=email-activity';
}

/** Customer document alerts open the workspace; older alerts saved only the document. */
async function resolveReferencePath(notification: NotificationRow): Promise<string | null> {
  const type = String(notification.type || '').trim();
  if (type === 'survey_integrity') return '/admin?tab=survey-integrity';
  const meta = notification.metadata && typeof notification.metadata === 'object' ? notification.metadata : {};
  const documentId = String(meta.documentId || '').trim();
  let projectId = String(meta.projectId || meta.project_id || '').trim();
  let kind = String(meta.kind || '').trim();
  if (documentId && (!projectId || !kind)) {
    const { data } = await directSupabase
      .from('documents')
      .select('project_id, kind')
      .eq('id', documentId)
      .maybeSingle();
    const row = data as { project_id?: string | null; kind?: string | null } | null;
    projectId = projectId || String(row?.project_id || '').trim();
    kind = kind || String(row?.kind || '').trim();
  }
  const workspacePath = workspaceDocumentPath({ id: documentId, kind, projectId });
  if (workspacePath) return workspacePath;
  return documentId ? `/docs/${encodeURIComponent(documentId)}` : null;
}

const JOBS_FILTER_KEY = 'jobs';

export const ActivityNotifications: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const { loading: profileLoading, isSiteCrew, isSupplier, canAccessAdmin, profile } = useProfile();
  const viewerRole = profile?.role || null;
  const hideForViewer = profileLoading || isSupplier;
  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const [enabled, setEnabled] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [assignedJobCardIds, setAssignedJobCardIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [centerOpen, setCenterOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [minimisedIds, setMinimisedIds] = useState<Record<string, boolean>>(() => readMinimisedIds());
  const [crewAlertOpen, setCrewAlertOpen] = useState(false);
  const canSendCrewAlerts = isOfficeJobViewerRole(viewerRole) && !isSiteCrew;

  const openJobPath = (notification: NotificationRow): string | null => {
    const meta = notification?.metadata && typeof notification.metadata === 'object'
      ? notification.metadata
      : {};
    return jobNotificationPath({
      viewerRole,
      jobCardId: (meta as any).jobCardId,
      projectId: (meta as any).projectId,
    });
  };

  // Click-outside handler for the notification panel
  useEffect(() => {
    if (!centerOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const targetEl = e.target as HTMLElement;

      // Don't close if clicking inside a MUI listbox (Select dropdown portal)
      if (targetEl.closest?.('[role="listbox"]') || targetEl.closest?.('[role="option"]')) {
        return;
      }

      // Close if clicking outside both panel and bell button
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        bellRef.current &&
        !bellRef.current.contains(target)
      ) {
        setCenterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [centerOpen]);

  useEffect(() => {
    const { data } = directSupabase.auth.onAuthStateChange((_event, session) => {
      setEnabled(!!session?.user?.id);
    });
    directSupabase.auth.getSession().then(({ data: { session } }) => setEnabled(!!session?.user?.id));
    return () => { data.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!enabled || hideForViewer || !isSiteCrew || !user?.id) {
      setAssignedJobCardIds([]);
      return;
    }
    let cancelled = false;
    listAssignedJobCardIds(user.id)
      .then((ids) => {
        if (!cancelled) setAssignedJobCardIds(ids);
      })
      .catch(() => {
        if (!cancelled) setAssignedJobCardIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, hideForViewer, isSiteCrew, user?.id]);

  const load = async () => {
    if (!enabled || hideForViewer) return;
    try {
      setLoading(true);
      // Office alerts are one row per person, so load only the viewer's own copies
      // separately rather than letting everyone's copies fill the latest-75 window.
      const [{ data: notifs }, { data: alerts }] = await Promise.all([
        directSupabase
          .from('notifications')
          .select('*')
          .neq('type', CREW_ALERT_TYPE)
          .order('created_at', { ascending: false })
          .limit(75),
        user?.id
          ? directSupabase
            .from('notifications')
            .select('*')
            .eq('type', CREW_ALERT_TYPE)
            .eq('metadata->>recipientId', user.id)
            .order('created_at', { ascending: false })
            .limit(25)
          : Promise.resolve({ data: [] as any[] }),
      ]);
      setNotifications([...(notifs || []), ...(alerts || [])] as any);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled || hideForViewer) return;
    load();

    const channel = directSupabase
      .channel('notifications-flyout')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, (payload: any) => {
        setNotifications((prev) => [payload.new as NotificationRow, ...prev]);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'notifications' }, (payload: any) => {
        setNotifications((prev) => prev.map(n => (n.id === payload.new.id ? (payload.new as NotificationRow) : n)));
      })
      .subscribe();

    return () => {
      directSupabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, hideForViewer]);

  const activityItems = useMemo<ActivityItem[]>(() => {
    const notifItems: ActivityItem[] = notifications
      .filter((n) => {
        const type = String(n.type || '').trim();
        // Office alerts are one row per recipient: only that person sees their copy
        if (isCrewAlertType(type)) return crewAlertVisible({ viewerId: user?.id, metadata: n.metadata });
        if (isJobNotificationType(type)) {
          const meta = n.metadata && typeof n.metadata === 'object' ? n.metadata : {};
          return jobNotificationVisible({
            viewerRole,
            viewerId: user?.id,
            assignedJobCardIds: isSiteCrew ? assignedJobCardIds : undefined,
            type,
            actorId: (meta as any).actorId,
            audience: (meta as any).audience,
            jobCardId: (meta as any).jobCardId,
          });
        }
        if (ADMIN_ONLY_NOTIFICATION_TYPES.has(type)) return viewerRole === 'admin';
        if (isSiteCrew) return false;
        return WIDGET_NOTIFICATION_TYPES.has(type);
      })
      .map((n) => ({
        source: 'notification',
        id: n.id,
        createdAt: n.created_at,
        typeKey: String(n.type || '').trim() || 'notification',
        typeLabel: isCrewAlertType(n.type)
          ? crewAlertLabel(n.metadata)
          : labelForType(String(n.type || 'notification')),
        message: n.message,
        isArchived: !!n.is_read,
        raw: n,
      }));

    return notifItems.sort((a, b) => (Date.parse(b.createdAt) - Date.parse(a.createdAt)));
  }, [assignedJobCardIds, isSiteCrew, notifications, user?.id, viewerRole]);

  const unread = useMemo(
    () => activityItems.filter((item) => !item.isArchived && !minimisedIds[item.id]).length,
    [activityItems, minimisedIds]
  );

  const popupItems = useMemo(() => {
    return activityItems
      .filter((item) => !item.isArchived)
      .filter((item) => !minimisedIds[item.id])
      .slice(0, 3);
  }, [activityItems, minimisedIds]);

  const renderNotificationMessage = (n: NotificationRow) => {
    const message = String(n.message || '');
    const type = String(n.type || '').trim();
    if (!LINKABLE_REF_NOTIFICATION_TYPES.has(type)) return message;

    const refFromMeta = String(n.metadata?.invoiceRef || n.metadata?.lifecycleRef || '').trim();
    const reference = refFromMeta || parseReferenceFromMessage(message);
    if (!reference) return message;

    const projectId = String(n.metadata?.projectId || '').trim();
    const documentId = String(n.metadata?.documentId || '').trim();
    if (type !== 'survey_integrity' && !projectId && !documentId) return message;

    const idx = message.indexOf(reference);
    if (idx < 0) return message;

    const before = message.slice(0, idx);
    const after = message.slice(idx + reference.length);

    return (
      <>
        {before}
        <Link
          component="button"
          underline="always"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void resolveReferencePath(n).then((path) => { if (path) navigate(path); });
          }}
          sx={{ p: 0, minHeight: 'unset', font: 'inherit', verticalAlign: 'baseline' }}
        >
          {reference}
        </Link>
        {after}
      </>
    );
  };

  const allTypes = useMemo(() => {
    const preferredOrder = ['lead_created', 'doc_opened', 'doc_accepted', 'doc_rejected', 'doc_query'];
    const out = isSiteCrew
      ? []
      : preferredOrder
        .filter((t) => WIDGET_NOTIFICATION_TYPES.has(t))
        .map((t) => ({ key: t, label: `Notification · ${labelForType(t)}` }));

    if (isSiteCrew || isOfficeJobViewerRole(viewerRole)) {
      out.push({ key: JOBS_FILTER_KEY, label: 'Jobs' });
    }
    if (isSiteCrew) {
      out.push({ key: CREW_ALERT_TYPE, label: 'Office alerts' });
    }
    if (viewerRole === 'admin') {
      out.push({ key: 'survey_integrity', label: `Notification · ${labelForType('survey_integrity')}` });
    }

    // Include any additional widget-enabled types that may be introduced later.
    notifications.forEach((n) => {
      const t = String(n.type || '').trim();
      if (!t || isJobNotificationType(t) || !WIDGET_NOTIFICATION_TYPES.has(t)) return;
      if (isSiteCrew) return;
      if (out.some((x) => x.key === t)) return;
      out.push({ key: t, label: `Notification · ${labelForType(t)}` });
    });

    return out;
  }, [isSiteCrew, notifications, viewerRole]);

  const filtered = useMemo(() => {
    return activityItems
      .filter(item => {
        // Exclude minimised items
        if (minimisedIds[item.id]) return false;
        return true;
      })
      .filter((item) => {
        if (typeFilter === 'all') return true;
        if (typeFilter === JOBS_FILTER_KEY) return isJobNotificationType(item.typeKey);
        return item.typeKey === typeFilter;
      });
  }, [activityItems, minimisedIds, typeFilter]);

  const limited = useMemo(() => filtered.slice(0, CENTER_LIMIT), [filtered]);

  const minimise = async (id: string) => {
    // Persist dismissal as "read" in Supabase so it sticks across sessions/devices.
    try {
      await directSupabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id);
    } catch {
      // Ignore failures — local minimised state still applies.
    }

    const next = { ...minimisedIds, [id]: true };
    setMinimisedIds(next);
    writeMinimisedIds(next);
  };

  if (!enabled || hideForViewer) return null;

  return (
    <>
      {/* Popups bottom-right */}
      <Box
        sx={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 2000,
          width: { xs: 'calc(100vw - 32px)', sm: 380 },
          pointerEvents: 'none',
        }}
      >
        <Stack spacing={1} sx={{ alignItems: 'stretch' }}>
          {popupItems.map((n) => {
            const Icon = iconForType(n.typeKey);
            return (
              <Card
                key={`${n.source}-${n.id}`}
                variant="outlined"
                sx={{
                  pointerEvents: 'auto',
                  boxShadow: 'lg',
                  borderColor: 'rgba(0,0,0,0.15)',
                  bgcolor: '#fff',
                }}
              >
                <Stack spacing={0.75}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <IconWrapper icon={Icon} />
                      <Chip
                        size="sm"
                        variant="soft"
                        color={chipColorForType(n.raw.type, n.raw.metadata)}
                      >
                        {n.typeLabel}
                      </Chip>
                      <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)' }}>
                        {format(new Date(n.createdAt), 'PP p')}
                      </Typography>
                    </Stack>
                    <IconButton size="sm" variant="plain" onClick={() => minimise(n.id)} aria-label="Minimise notification">
                      <IconWrapper icon={FaTimes} />
                    </IconButton>
                  </Stack>

                  <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.78)' }}>
                    {renderNotificationMessage(n.raw)}
                  </Typography>
                  {String(n.raw.type || '').trim() === 'doc_query' && (
                    <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.78)' }}>
                      <Link
                        component="button"
                        underline="always"
                        onClick={(e) => {
                          (async () => {
                            e.preventDefault();
                            e.stopPropagation();
                            const detailPath = await resolveActivityPreviewPath(n.raw);
                            navigate(detailPath);
                          })();
                        }}
                        sx={{ p: 0, minHeight: 'unset', font: 'inherit', color: 'primary.600', verticalAlign: 'baseline' }}
                      >
                        View Query
                      </Link>
                    </Typography>
                  )}

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {isJobNotificationType(String(n.raw.type || '').trim()) && openJobPath(n.raw) ? (
                      <Button size="sm" variant="plain" onClick={() => navigate(openJobPath(n.raw) as string)}>
                        Open job
                      </Button>
                    ) : String(n.raw.type || '').trim() === 'lead_created' ? (
                      <Button size="sm" variant="plain" onClick={() => navigate('/leads')}>
                        Open leads
                      </Button>
                    ) : (
                      <Button size="sm" variant="plain" onClick={() => setCenterOpen(true)}>
                        View all
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Card>
            );
          })}

          {/* Floating bell */}
          <Sheet
            ref={bellRef}
            variant="solid"
            sx={{
              pointerEvents: 'auto',
              alignSelf: 'flex-end',
              mt: popupItems.length ? 0.5 : 0,
              borderRadius: 999,
              boxShadow: 'lg',
              px: 1.25,
              py: 0.9,
              bgcolor: '#fcc14d',
              color: '#212D3B',
              border: '1px solid rgba(33,45,59,0.12)',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => setCenterOpen((prev) => !prev)}
                sx={{ color: '#212D3B' }}
                aria-label="Open activity notifications"
              >
                <IconWrapper icon={FaBell} />
              </IconButton>
              <Typography level="body-sm" sx={{ fontWeight: 800 }}>
                {unread} unread
              </Typography>
            </Stack>
          </Sheet>

          {/* Notification Center Panel */}
          {centerOpen && (
            <Sheet
              ref={panelRef}
              variant="outlined"
              sx={{
                pointerEvents: 'auto',
                position: 'absolute',
                right: 0,
                bottom: 56,
                width: { xs: 'calc(100vw - 32px)', sm: 380 },
                maxHeight: 'calc(100vh - 100px)',
                borderRadius: 'lg',
                boxShadow: 'xl',
                overflow: 'hidden',
                bgcolor: 'background.surface',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Header */}
              <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography level="title-md" sx={{ fontWeight: 600 }}>Activity (latest 10)</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {canSendCrewAlerts && (
                      <Button
                        size="sm"
                        variant="soft"
                        startDecorator={<IconWrapper icon={FaBullhorn} />}
                        onClick={() => {
                          setCenterOpen(false);
                          setCrewAlertOpen(true);
                        }}
                      >
                        Crew alert
                      </Button>
                    )}
                    <IconButton size="sm" variant="plain" onClick={() => setCenterOpen(false)} aria-label="Close">
                      <IconWrapper icon={FaTimes} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>

              {/* Content */}
              <Box sx={{ p: 1.5, overflow: 'auto', flex: 1 }}>
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Select
                      size="sm"
                      value={typeFilter}
                      onChange={(_, v) => setTypeFilter(String(v || 'all'))}
                      sx={{ flex: 1 }}
                      slotProps={{
                        listbox: {
                          sx: { zIndex: 2100 },
                        },
                      }}
                    >
                      <Option value="all">All types</Option>
                      {allTypes.map(t => <Option key={t.key} value={t.key}>{t.label}</Option>)}
                    </Select>
                  </Stack>

                  <Divider />

                  {loading ? (
                    <Typography level="body-sm">Loading…</Typography>
                  ) : limited.length === 0 ? (
                    <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                      No notifications.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {limited.map((item) => {
                        const Icon = iconForType(item.typeKey);
                        return (
                          <Card
                            key={`${item.source}-${item.id}`}
                            variant="outlined"
                            sx={{
                              borderColor: item.isArchived ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.15)',
                              boxShadow: item.isArchived ? 'none' : 'sm',
                              opacity: item.isArchived ? 0.7 : 1,
                            }}
                          >
                            <Stack spacing={0.75}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flexWrap: 'wrap' }}>
                                  <IconWrapper icon={Icon} />
                                  <Chip size="sm" variant="soft" color={chipColorForType(item.typeKey, item.raw.metadata)}>
                                    {item.typeLabel}
                                  </Chip>
                                  {item.source === 'notification' && !item.isArchived && (
                                    <Chip size="sm" variant="solid" color="primary">New</Chip>
                                  )}
                                </Stack>
                                <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)', whiteSpace: 'nowrap' }}>
                                  {format(new Date(item.createdAt), 'PP p')}
                                </Typography>
                              </Stack>

                              <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.78)' }}>
                                {renderNotificationMessage(item.raw)}
                              </Typography>
                              {String(item.raw.type || '').trim() === 'doc_query' && (
                                <Typography level="body-sm" sx={{ color: 'rgba(0,0,0,0.78)' }}>
                                  <Link
                                    component="button"
                                    underline="always"
                                    onClick={(e) => {
                                      (async () => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const detailPath = await resolveActivityPreviewPath(item.raw);
                                        navigate(detailPath);
                                      })();
                                    }}
                                    sx={{ p: 0, minHeight: 'unset', font: 'inherit', color: 'primary.600', verticalAlign: 'baseline' }}
                                  >
                                    View Query
                                  </Link>
                                </Typography>
                              )}

                              <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                {isJobNotificationType(String(item.raw.type || '').trim()) && openJobPath(item.raw) && (
                                  <Button
                                    size="sm"
                                    variant="plain"
                                    onClick={() => {
                                      const path = openJobPath(item.raw);
                                      setCenterOpen(false);
                                      if (path) navigate(path);
                                    }}
                                  >
                                    Open job
                                  </Button>
                                )}
                                {String(item.raw.type || '').trim() === 'lead_created' && (
                                  <Button
                                    size="sm"
                                    variant="plain"
                                    onClick={() => {
                                      setCenterOpen(false);
                                      navigate('/leads');
                                    }}
                                  >
                                    Open leads
                                  </Button>
                                )}
                                <Button size="sm" variant="outlined" onClick={() => minimise(item.id)}>
                                  Dismiss
                                </Button>
                              </Stack>
                            </Stack>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </Box>

              {/* Footer */}
              <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography level="body-xs" sx={{ color: 'rgba(0,0,0,0.55)' }}>
                    Showing latest {CENTER_LIMIT} items.
                  </Typography>
                  {canAccessAdmin && (
                    <Button
                      size="sm"
                      variant="plain"
                      onClick={() => {
                        setCenterOpen(false);
                        navigate('/admin?tab=email-activity');
                      }}
                    >
                      Go to Activity
                    </Button>
                  )}
                </Stack>
              </Box>
            </Sheet>
          )}
        </Stack>
      </Box>
      {canSendCrewAlerts && (
        <CrewAlertModal
          open={crewAlertOpen}
          onClose={() => setCrewAlertOpen(false)}
          sender={{ id: user?.id || null, name: profile?.full_name || user?.email || null }}
        />
      )}
    </>
  );
};

export default ActivityNotifications;

